import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SECTIONS = ["A", "B", "C", "D"];

export default function AttendanceReport() {
  const [programs, setPrograms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [percentage, setPercentage] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [monthHeaders, setMonthHeaders] = useState([]);
  const [logoBase64, setLogoBase64] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Get user info from session
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const showError = (message) => {
    setErrorMessage(message);
  };

  useEffect(() => {
    if (clientId && collegeId) {
      setLoading(true);
      axios
        .get(`${API_BASE_URL}/api/attendancereport/programs/${clientId}/${collegeId}`)
        .then((res) => setPrograms(res.data.data || []))
        .catch(() => {
          setPrograms([]);
          showError("Unable to load programs. Please check the server connection.");
        })
        .finally(() => setLoading(false));
    }
  }, [clientId, collegeId]);

  useEffect(() => {
    if (selectedProgram && clientId && collegeId) {
      setLoading(true);
      axios
        .get(
          `${API_BASE_URL}/api/attendancereport/branches/${clientId}/${collegeId}/${selectedProgram}`
        )
        .then((res) => setBranches(res.data.data || []))
        .catch(() => {
          setBranches([]);
          showError("Unable to load branches. Please check the server connection.");
        })
        .finally(() => setLoading(false));
      setSelectedBranch("");
      setBatches([]);
      setSelectedBatch("");
      setSelectedSection("");
    }
  }, [selectedProgram, clientId, collegeId]);

  useEffect(() => {
    if (selectedBranch && selectedProgram && clientId && collegeId) {
      setLoading(true);
      axios
        .get(
          `${API_BASE_URL}/api/attendancereport/batches/${clientId}/${collegeId}/${selectedProgram}/${selectedBranch}`
        )
        .then((res) => setBatches(res.data.data || []))
        .catch(() => {
          setBatches([]);
          showError("Unable to load batches. Please check the server connection.");
        })
        .finally(() => setLoading(false));
      setSelectedBatch("");
      setSelectedSection("");
    }
  }, [selectedBranch, selectedProgram, clientId, collegeId]);

  const handleGetReport = () => {
    setErrorMessage("");
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/attendancereport/report`, {
        params: {
          client_id: clientId,
          college_id: collegeId,
          program_id: selectedProgram,
          branch_id: selectedBranch,
          admittedbatch_id: selectedBatch,
          section: selectedSection || undefined,
          from_date: fromDate,
          to_date: toDate,
          percentage,
          amount,
        },
      })
      .then((res) => {
        if (res.data && res.data.data) {
          setReportData(res.data.data.report || []);
          setMonthHeaders(res.data.data.monthHeaders || []);
        } else {
          setReportData([]);
          setMonthHeaders([]);
        }
      })
      .catch((err) => {
        setReportData([]);
        setMonthHeaders([]);
        showError(err.response?.data?.message || "Unable to load attendance report.");
      })
      .finally(() => setLoading(false));
  };

  const handleDownloadPDF = async () => {
    try {
      const params = {
        client_id: clientId,
        college_id: collegeId,
        program_id: selectedProgram,
        branch_id: selectedBranch,
        admittedbatch_id: selectedBatch,
        section: selectedSection,
        from_date: fromDate,
        to_date: toDate,
        percentage,
        amount,
        logoBase64,
      };
      const response = await fetch(`${API_BASE_URL}/api/attendancereport/download-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/pdf" },
        body: JSON.stringify(params),
      });
      if (!response.ok) throw new Error("Failed to download PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "AttendanceReport.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download PDF: " + err.message);
    }
  };

  useEffect(() => {
    async function fetchLogo() {
      try {
        // Try to get logo params from first student row's college info if available
        let logoParams = null;
        if (
          reportData &&
          reportData.length > 0 &&
          reportData[0].college_logopath &&
          reportData[0].society_id &&
          reportData[0].college_id
        ) {
          logoParams = {
            clientId,
            society_id: reportData[0].society_id,
            college_id: reportData[0].college_id,
            college_logopath: reportData[0].college_logopath,
          };
        }
        // fallback: try from user session (if available)
        if (!logoParams && user && user.society_id && user.college_id && user.college_logopath) {
          logoParams = {
            clientId,
            society_id: user.society_id,
            college_id: user.college_id,
            college_logopath: user.college_logopath,
          };
        }
        if (!logoParams) {
          setLogoBase64(null);
          return;
        }
        const query = new URLSearchParams(logoParams).toString();
        const res = await fetch(`${API_BASE_URL}/api/attendancereport/logo?${query}`);
        const data = await res.json();
        if (data.success) setLogoBase64(data.logoBase64);
        else setLogoBase64(null);
      } catch (e) {
        setLogoBase64(null);
      }
    }
    fetchLogo();
  }, [reportData, clientId]);

  const columns = [
    { field: "s_no", headerName: "S.No", width: 70 },
    { field: "roll_no", headerName: "RollNo", width: 120 },
    { field: "student_name", headerName: "StudentName", width: 400 },
    ...monthHeaders,
    { field: "total", headerName: "Total", width: 100 },
    { field: "present_percent", headerName: "Present %", width: 100 },
    { field: "absent_percent", headerName: "Absent % to 75%", width: 150 },
    { field: "fine_amount", headerName: "Fine Amount", width: 120 },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar>
        <Typography variant="h6">Attendance Report</Typography>
      </Toolbar>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <TextField
          label="Program"
          name="program"
          select
          value={selectedProgram}
          onChange={(e) => setSelectedProgram(e.target.value)}
          size="medium"
          sx={{ minWidth: 180 }}
          disabled={false}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Program</option>
          {programs.map((p) => (
            <option key={p.program_id} value={p.program_id}>
              {p.program_name}
            </option>
          ))}
        </TextField>

        <TextField
          label="Branch"
          name="branch"
          select
          value={selectedBranch}
          onChange={(e) => setSelectedBranch(e.target.value)}
          size="medium"
          sx={{ minWidth: 180 }}
          disabled={!selectedProgram}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Branch</option>
          {branches.map((b) => (
            <option key={b.branch_id} value={b.branch_id}>
              {b.branch_name}
            </option>
          ))}
        </TextField>

        <TextField
          label="Batch"
          name="batch"
          select
          value={selectedBatch}
          onChange={(e) => setSelectedBatch(e.target.value)}
          size="medium"
          sx={{ minWidth: 180 }}
          disabled={!selectedBranch}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Batch</option>
          {batches.map((batch) => (
            <option key={batch.admittedbatch_id} value={batch.admittedbatch_id}>
              {batch.admittedbatch}
            </option>
          ))}
        </TextField>

        <TextField
          label="Section"
          name="section"
          select
          value={selectedSection}
          onChange={(e) => setSelectedSection(e.target.value)}
          size="medium"
          sx={{ minWidth: 100 }}
          disabled={!selectedBatch}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Section</option>
          {SECTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </TextField>

        <TextField
          label="From Date"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          size="medium"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />

        <TextField
          label="To Date"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          size="medium"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />

        <TextField
          label="Percentage"
          value={percentage}
          onChange={(e) => setPercentage(e.target.value)}
          size="medium"
          sx={{ minWidth: 100 }}
        />

        <TextField
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          size="medium"
          sx={{ minWidth: 100 }}
        />

        <Button
          variant="contained"
          onClick={handleGetReport}
          disabled={loading}
          sx={{ color: "#fff", minWidth: 120 }}
        >
          {loading ? <CircularProgress size={24} /> : "Get Report"}
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={handleDownloadPDF}
          sx={{ color: "#fff", minWidth: 120 }}
        >
          Download PDF
        </Button>
      </Box>

      <Box sx={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={reportData.map((row, idx) => ({ id: idx, ...row }))}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          loading={loading}
          getRowId={(row) => row.s_no || row.id}
          disableSelectionOnClick
        />
      </Box>
    </Paper>
  );
}
